import { userTimezone, TRAVEL_NEEDS_INFO } from '../../../helper';

/**
 * Form metadata for the program entry form.
 *
 * Replaces `Program Entry Updates/src/types/program.ts`. That file was half TypeScript
 * types (nothing to port — this app is plain JS) and half runtime data: the section list,
 * the initial values, the required-field map and the field labels. Those four live here.
 *
 * Field names are the *app's*, not the design's — `noOfSlots` not `availableSlots`,
 * `expLevel` not `experienceLevel`, `byDates` not `priceBasis`. Keeping them identical to
 * `AddProgramModal.js` is what lets handleSubmit and the edit hydration port verbatim.
 */

/**
 * Re-exported from `helper.js`, which now owns it — the camper-facing pages gate the
 * Travel Info block on the same rule and should not import from the director form. Kept
 * exported here so `validation.js` and `sections/Amenities.js` import paths are unchanged.
 */
export { TRAVEL_NEEDS_INFO };

// Section order follows the design, which promotes Timing above Location. The app has it
// sixth; nothing depends on the order.
export const SECTION_ORDER = [
    { id: 'description', label: 'Description', blurb: 'Name, category and program copy' },
    { id: 'groups', label: 'Applicable Groups', blurb: 'Who this program is for' },
    { id: 'price', label: 'Price', blurb: 'Registration windows and pricing' },
    { id: 'schedule', label: 'Program Dates and Time', blurb: 'Dates and check in / out' },
    { id: 'location', label: 'Location', blurb: 'Where the program takes place' },
    { id: 'amenities', label: 'Amenities', blurb: 'Meals, gear and facilities' },
    { id: 'extra', label: 'Extra', blurb: 'Discounts, visibility, questions' },
];

/**
 * A per-program code, as `AddProgramModal.js:427-435` generates it.
 *
 * Seeded into the initial state rather than written from an effect after mount. That write
 * was indistinguishable from a user edit, so a brand-new form counted as having unsaved
 * changes and warned on close before anything had been typed. On edit the hydrated code
 * replaces this one.
 */
export const randomProgramCode = () => {
    let programCode = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    for (let i = 0; i < 7; i++) {
        programCode += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return programCode;
};

/**
 * Initial `userInput`, copied field for field from `AddProgramModal.js:134-202`.
 *
 * A function rather than a constant because `programTimezone` resolves from the browser,
 * `programCode` is random, and because each mount needs its own object.
 */
export const initialUserInput = () => ({
    programName: '',
    programId: '',
    programCode: randomProgramCode(),
    noOfSlots: '',
    registerNonRefundFee: '',
    dateEarlyRegisterOpen: '',
    dateEarlyRegisterClosed: '',
    earlyBirdPrice: 0,
    registerOpenDate: '',
    registerCloseDate: '',
    registrationFee: 0,
    commuterPrice: 0,
    // Optional per-tier overrides. Empty means "inherit commuterPrice" on the server, so
    // these start blank rather than 0 — a 0 in the box would read as a real $0 price.
    earlyBirdCommuterPrice: '',
    lateCommuterPrice: '',
    dateLateRegisterOpen: '',
    dateLateRegisterClosed: '',
    lateRegistrationFee: '',
    category: '',
    subCategory: '',
    addressLine1: '',
    city: '',
    state: '',
    zipCode: '',
    gender: '',
    campId: '',
    programType: '',
    minAge: '',
    maxAge: '',
    grade: '',
    expLevel: '',
    startDate: '',
    endDate: '',
    checkInTime: '',
    checkOutTime: '',
    attendanceHour: '',
    meals: '',
    tshirt: '',
    equipment: '',
    facility: '',
    travel: '',
    travelInfo: '',
    scholarships: '',
    discount: '',
    impNotes: '',
    questions: [],
    counselors: [],
    programIntroduction: '',
    aboutProgram: '',
    programInstruction: '',
    disclaimer: '',
    pdf: '',
    lat: '',
    long: '',
    scholarshipsUrl: '',
    isfeatured: '',
    isprivate: '',
    discountType: 1,
    isOnline: '',
    tax: '',
    earlyConvenienceFee: '',
    convenienceFee: '',
    lateConvenienceFee: '',
    siblingDiscount: '',
    siblingDiscountType: '',
    multiProgramDiscount: '',
    multiProgramDiscountType: '',
    programTimezone: userTimezone,
    disableOvernight: false,
    startDateFormatted: '',
    endDateFormatted: '',

    // Held as separate `useState` in the modal (`:111`). Folded in here so the progress
    // meter can count it like any other required field. It drives PlacesAutocomplete and
    // populates lat/long/address on select; it is never sent in the payload.
    location: '',
});

// Errors mirror `AddProgramModal.js:206-254`. Values are message strings, '' when clean.
export const initialError = () => ({
    programName: '',
    programCode: '',
    noOfSlots: '',
    registerOpenDate: '',
    registerCloseDate: '',
    registrationFee: '',
    category: '',
    subCategory: '',
    addressLine1: '',
    city: '',
    state: '',
    gender: '',
    campId: '',
    programType: '',
    minAge: '',
    maxAge: '',
    grade: '',
    expLevel: '',
    startDate: '',
    endDate: '',
    checkInTime: '',
    checkOutTime: '',
    tshirt: '',
    equipment: '',
    facility: '',
    travel: '',
    travelInfo: '',
    scholarships: '',
    impNotes: '',
    questions: '',
    programIntroduction: '',
    aboutProgram: '',
    programInstruction: '',
    disclaimer: '',
    lat: '',
    long: '',
    location: '',
    tax: '',
    counselors: '',
    siblingDiscount: '',
    siblingDiscountType: '',
    multiProgramDiscount: '',
    multiProgramDiscountType: '',
    zipCode: '',
    scholarshipsUrl: '',
    programTimezone: '',
    commuterPrice: '',
});

// A single empty row of the `additionalDates` table (`AddProgramModal.js:122-130`).
//
// `commuterPrice` is not in the modal's version, even though the row renders an input for it
// and the value round-trips to the API inside `JSON.stringify(additionalDates)`. A new row
// therefore started `undefined` and only gained the key once someone typed in it. Seeded here
// so every row has the same shape.
export const emptyAdditionalDate = () => ({
    startDate: '',
    endDate: '',
    standardPrice: 0,
    slots: 0,
    commuterPrice: 0,
    endDateErr: '',
    standardPriceErr: '',
    slotsErr: '',
});

/**
 * Which fields the progress meter counts per section.
 *
 * This is every label carrying a `*` in the old form, grouped by section, with the same
 * render gates the old form applies — a field that does not render is not counted.
 *
 * One of these is stricter than `handleValidate` was: `programType`, whose check sits
 * commented out at `AddProgramModal.js:744-749`. That was agreed in the decisions doc.
 *
 * `commuterPrice` is deliberately absent. The design marked it `*` and decisions doc §14
 * enforced it, but not every camp offers a commuter option, so the client reversed that —
 * an empty commuter price is now a valid program.
 *
 * @param values current userInput
 * @param byDates 1 = priced by program dates, 2 = priced by registration windows
 */
export const getRequiredFields = (values, byDates) => {
    const usesProgramDates = byDates === 1;

    return {
        description: [
            'programName',
            'category',
            'subCategory',
            'programType',
            'aboutProgram',
            'disclaimer',
        ],
        groups: ['gender', 'expLevel', 'minAge', 'maxAge', 'grade'],
        // With program dates the price fields move into the additionalDates rows, which
        // are validated per row rather than counted here.
        price: usesProgramDates
            ? []
            : ['noOfSlots', 'registerOpenDate', 'registerCloseDate', 'registrationFee'],
        schedule: usesProgramDates
            ? ['checkInTime', 'checkOutTime']
            : ['startDate', 'endDate', 'checkInTime', 'checkOutTime'],
        // The whole address block sits behind `isOnline ? '' : <Fragment>`
        // (`AddProgramModal.js:2147`), timezone included.
        location: values.isOnline
            ? []
            : ['location', 'programTimezone', 'addressLine1', 'city', 'state'],
        // Travel Info is asked for when the camp is moving campers itself — bus (1) or
        // shuttle (3). Not included (2) and family drop-off (4) have nothing to describe.
        amenities: TRAVEL_NEEDS_INFO.includes(values.travel)
            ? ['tshirt', 'equipment', 'facility', 'scholarships', 'travel', 'travelInfo']
            : ['tshirt', 'equipment', 'facility', 'scholarships', 'travel'],
        extra: [],
    };
};

// Used by the "fix these first" banner so it can name a missing field in the user's terms.
// Labels are taken verbatim from the old form, minus the asterisk.
export const FIELD_LABELS = {
    programName: 'Camp Program',
    category: 'Program Category',
    subCategory: 'Program Sub Category',
    programType: 'Program Type',
    aboutProgram: 'About Program',
    disclaimer: 'Disclaimer Statement',
    gender: 'Gender',
    expLevel: 'Experience Level',
    minAge: 'Age Group (Min)',
    maxAge: 'Age Group (Max)',
    grade: 'Grade',
    noOfSlots: 'No. of Available slots',
    registerOpenDate: 'Date Standard Registration Start',
    registerCloseDate: 'Date Standard Registration End',
    registrationFee: 'Standard Price',
    commuterPrice: 'Commuter Price',
    startDate: 'Start Date',
    endDate: 'End Date',
    checkInTime: 'Check-In Time',
    checkOutTime: 'Check-Out Time',
    location: 'Location',
    programTimezone: 'Program Timezone',
    addressLine1: 'Street Address',
    city: 'City',
    state: 'State',
    zipCode: 'Zip Code',
    tshirt: 'T-shirt',
    equipment: 'Equipment Needed',
    facility: 'Facility',
    scholarships: 'Scholarships',
    scholarshipsUrl: 'Scholarships Url',
    travel: 'Travel',
    travelInfo: 'Travel Info',
};
